import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getLocalStorageItem } from "../Catalog";
import { toast } from "sonner"; // Add toast import
import { useNavigate } from "react-router-dom"; // Add navigation import
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { sharedQueries } from "@surgia/api/src/queryStore/shared";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { permissions } from "../../permissions";

// components
import { GenericTableHeader } from "@surgia/ui";
import Button from "@surgia/ui/components/button";
import TableControls from "@surgia/ui/containers/table/table-controls";
import GenericModal from "@surgia/ui/components/genericModal";
import HeaderLayout from "@surgia/ui/layouts/Header";
import PurchaseRequestTable from "../../containers/tables/purchaseRequest-table";
import PurchaseRequestCart from "../../containers/PurchaseRequestCart";
import PurchaseRequestDetailsModal from "../../containers/Modals/PurchaseRequestDetailsModal";
import ConfirmationModal from "@surgia/ui/components/confirmationModal"; // Add confirmation modal import
import { useOutletContext } from "react-router-dom";
import { useGetPurchaseRequestListings } from "../../hooks/get/useGetPurchaseRequestListings";
import { HUB_PROTECTED_ROUTES } from "../../routes"; // Add routes import
import CreatePurchaseOrderModal from "../../containers/Modals/CreatePurchaseOrderModal";

// icons
import addIcon from "@surgia/ui/icons/actions/add.svg";

// constants
const ITEMS_PER_PAGE = 8;
// ✅ Migrated: branchesIds moved inside component to use Redux

// Filters Modal Component
export const FiltersModal = ({
  open,
  onClose,
  onApply,
  filters,
  setFilters,
}: {
  open: boolean;
  onClose: () => void;
  onApply: () => void;
  filters: any;
  setFilters: (f: any) => void;
}) => {
  const { t } = useTranslation();
  const [localFilters, setLocalFilters] = React.useState(filters);

  // Keep localFilters in sync with parent filters when modal opens
  React.useEffect(() => {
    if (open) setLocalFilters(filters);
  }, [open, filters]);

  const handleApply = () => {
    setFilters(localFilters);
    onApply();
  };

  const handleClear = () => {
    setLocalFilters({
      ...localFilters,
      id: "",
      branches_ids: "",
    });
  };

  return (
    <GenericModal
      title={t("filters")}
      open={open}
      onClose={onClose}
      footer={
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            onClick={handleClear}
            className="text-[#0033CC] font-medium"
          >
            {t("admin.clearFilters")}
          </Button>
          <div className="flex-1" />
          <Button variant="outline" onClick={onClose}>
            {t("cancel")}
          </Button>
          <Button variant="primary" onClick={handleApply}>
            {t("filter")}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* ID Filter */}
        <div>
          <label className="block text-[14px] mb-1">
            {t("shared.table.id")}
          </label>
          <div className="relative">
            <input
              type="text"
              className="w-full border rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-gray-300"
              placeholder={t("admin.idRange")}
              value={localFilters.id || ""}
              onChange={(e) => {
                const value = e.target.value;
                if (/^[\d-]*$/.test(value) || value === "") {
                  setLocalFilters({ ...localFilters, id: value });
                }
              }}
              inputMode="numeric"
              pattern="[\d-]*"
            />
          </div>
        </div>
      </div>
    </GenericModal>
  );
};

// Main Purchase Request Component
const PurchaseRequest: React.FC = () => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const navigate = useNavigate(); // Add navigation hook

  // Get user permissions from global state
  const authState = useSelector((state: RootState) => state.auth);
  const userPermissions = authState.globalState.user_permissions?.map(
    (permission) => permission.name
  ) || [];

  // ✅ Migrated from module-level localStorage to Redux
  const branchesIds = authState.globalState.user_branches?.split(',').map(Number).filter(Boolean) || [];
  const allBranchesIds = branchesIds.join(",");

  interface OutletContextType {
    branchesDropdown: any[];
    selectedBranch: string | null;
    setSelectedBranch: (branch: string | null) => void;
    isbranchesDropDownLoading: boolean;
  }

  const {
    branchesDropdown,
    selectedBranch,
    setSelectedBranch,
    isbranchesDropDownLoading,
  } = useOutletContext<OutletContextType>();

  // Get tenant ID from localStorage
  const tenant_id = getLocalStorageItem("tenant_id", "");

  // Add draft PO query
  const { data: draftPO, isPending: isDraftPOPending } = useQuery({
    ...sharedQueries.purchaseOrder.queries.getCurrentDraftPO({
      tenantId: Number(tenant_id),
      componentId: import.meta.env.VITE_PUBLIC_COMPONENT_ID || "",
    }),
    enabled:
      !!tenant_id &&
      userPermissions?.includes(permissions.VIEW_DRAFT_PURCHASE_ORDER),
  });

  // Check if draft PO is empty
  const isDraftPOEmpty = !draftPO && !isDraftPOPending;

  // State management
  const [currentPage, setCurrentPage] = useState(0);
  const [openFilters, setOpenFilters] = useState(false);
  const [status, setStatus] = useState<string | undefined>("SUBMITTED");
  const [search, setSearch] = useState("");
  const [filters, setFilters] = useState({
    id: "",
    branches_ids: "",
  });
  const [showPurchaseCart, setShowPurchaseCart] = useState(false);
  const [selectedPurchaseRequests, setSelectedPurchaseRequests] = useState<
    string[]
  >([]);
  const [selectedPRForDetails, setSelectedPRForDetails] = useState<{
    prId: string | null;
    branchId: string | null;
  } | null>(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false); // Add confirmation modal state
  const [showCreatePOModal, setShowCreatePOModal] = useState(false);

  // Add sorting state with initial sort by ID descending (newest first)
  const [sortState, setSortState] = useState<{
    column: string | null;
    direction: "asc" | "desc" | null;
  }>({
    column: "id", // Initial sort column
    direction: "desc", // Initial sort direction (desc for newest first)
  });

  const statusTabs = [
    { label: "Submitted", value: "SUBMITTED" },
    { label: "Drafts", value: "DRAFT" },
    { label: "History", value: "ORDERED" },
  ];

  // Use the hook to fetch purchase request listings with sorting
  const { data: purchaseRequestListings, isLoading } =
    useGetPurchaseRequestListings({
      componentId: import.meta.env.VITE_PUBLIC_COMPONENT_ID || "",
      tenantId: tenant_id || "",
      branchesIds: selectedBranch || allBranchesIds,
      status: status as "SUBMITTED" | "DRAFT" | "ORDERED",
      page: currentPage,
      size: ITEMS_PER_PAGE,
      has_purchase_order: false,
      // Add sorting parameters
      sort:
        sortState.column && sortState.direction
          ? `${sortState.column},${sortState.direction}`
          : undefined,
    });

  // Filter and paginate the real data

  useEffect(() => {
    setCurrentPage(0);
  }, [search, status, filters.id]);

  // Handle sort changes
  const handleSort = (newSortState: {
    column: string | null;
    direction: "asc" | "desc" | null;
  }) => {
    setSortState(newSortState);
    setCurrentPage(0); // Reset to first page when sorting changes
  };

  // Handle opening purchase cart
  const handleOpenPurchaseCart = () => {
    if (isDraftPOEmpty) {
      setShowCreatePOModal(true);
      return;
    }
    navigate(HUB_PROTECTED_ROUTES.PURCHASE_ORDER_CREATION);
  };

  const handleViewPRDetails = (prId: string, branchId: string) => {
    setSelectedPRForDetails({ prId, branchId });
  };

  const handleAddSelected = () => {
    setShowConfirmationModal(true);
  };

  const queryClient = useQueryClient();

  const updatePurchaseOrderMutation = useMutation({
    ...sharedQueries.purchaseOrder.mutations.updatePurchaseOrderItem({
      componentId: import.meta.env.VITE_PUBLIC_COMPONENT_ID || "",
      tenantId: Number(tenant_id),
      body: {
        purchase_requests_ids: selectedPurchaseRequests.map((id) => Number(id)),
      },
    }),
    onSuccess: () => {
      toast.success("Purchase requests added successfully!", {
        description: `${selectedPurchaseRequests.length} purchase request(s) have been added to the purchase order.`,
      });
      setShowConfirmationModal(false);
      setSelectedPurchaseRequests([]);

      // Invalidate purchase request listings and draft PO
      queryClient.invalidateQueries({
        predicate: (query) => {
          const keyString = JSON.stringify(query.queryKey);
          return (
            (keyString.includes("purchaseRequest") &&
              keyString.includes("listings")) ||
            (keyString.includes("purchase-order") &&
              keyString.includes("draft"))
          );
        },
      });
    },
    onError: () => {
      toast.error("Failed to add purchase requests", {
        description: "An error occurred while adding purchase requests.",
      });
      setShowConfirmationModal(false);
      setSelectedPurchaseRequests([]);
    },
  });

  const handleConfirmAdd = () => {
    updatePurchaseOrderMutation.mutate();
  };

  return (
    <div dir={isRTL ? "rtl" : "ltr"}>
      <HeaderLayout
        title="Purchase Requests"
        subtitle="Manage and monitor all PRs"
        additionalStyles="justify-between"
        showBranchesDropDown={allBranchesIds?.includes(",")}
        branches={branchesDropdown}
        selectedBranch={selectedBranch}
        setSelectedBranch={setSelectedBranch}
        allUserBranches={allBranchesIds}
        showAllBranches={true}
        isBranchesLoading={isbranchesDropDownLoading}
      >
        {userPermissions.includes(
          permissions.EDIT_DRAFT_PURCHASE_ORDER ||
            permissions.VIEW_DRAFT_PURCHASE_ORDER
        ) && (
          <Button
            variant="primary"
            startIcon={addIcon}
            onClick={handleOpenPurchaseCart}
            disabled={
              isDraftPOPending ||
              (!userPermissions.includes(
                permissions.EDIT_DRAFT_PURCHASE_ORDER
              ) &&
                !!isDraftPOEmpty)
            }
          >
            {isDraftPOPending
              ? "Loading..."
              : !userPermissions.includes(
                    permissions.EDIT_DRAFT_PURCHASE_ORDER
                  ) && !!isDraftPOEmpty
                ? "View PO"
                : !!isDraftPOEmpty
                  ? "Create PO"
                  : "Edit PO "}
          </Button>
        )}
      </HeaderLayout>

      <div className="bg-white rounded-xl border border-gray-200 shadow pt-4">
        <GenericTableHeader
          title="Purchase Requests"
          totalCount={purchaseRequestListings?.total || 0}
          status={status}
          singularLabel="request"
          pluralLabel="requests"
        />

        <TableControls
          status={status}
          setStatus={setStatus}
          search={search}
          setSearch={setSearch}
          openFilters={openFilters}
          setOpenFilters={setOpenFilters}
          statusTabs={statusTabs}
          showFilter={false}
          showSearch={false}
        />

        {/* Purchase Requests Table Section */}
        <PurchaseRequestTable
          currentPage={currentPage}
          onPageChange={setCurrentPage}
          data={purchaseRequestListings?.data || []}
          totalPages={Math.ceil(
            (purchaseRequestListings?.total || 0) / ITEMS_PER_PAGE
          )}
          loading={isLoading}
          showBranchColumn={selectedBranch?.includes(",")}
          selectedPurchaseRequests={selectedPurchaseRequests}
          setSelectedPurchaseRequests={setSelectedPurchaseRequests}
          status={status}
          onViewDetails={handleViewPRDetails}
          sortState={sortState}
          onSort={handleSort}
          hasViewPermission={userPermissions.includes(
            permissions.VIEW_DRAFT_PURCHASE_REQUEST
          )}
          hasEditDraftPOPermission={userPermissions.includes(
            permissions.EDIT_DRAFT_PURCHASE_ORDER
          )}
        />
      </div>

      {/* Footer with selection info and actions */}
      {status === "SUBMITTED" && (
        <div className="w-full bg-white border-t border-gray-200 px-6 py-4">
          <div className="flex justify-between items-center max-w-7xl mx-auto">
            <span className="text-sm">
              Create Purchase Order
              <span className="text-sm text-gray-600 ml-1">
                {selectedPurchaseRequests?.length || 0} Selected
              </span>
            </span>
            <div className="flex space-x-2">
              <Button
                variant="outline"
                onClick={() => setSelectedPurchaseRequests([])}
                disabled={selectedPurchaseRequests.length === 0}
              >
                Dismiss
              </Button>
              <Button
                variant="primary"
                onClick={handleAddSelected} // Change this line
                disabled={
                  selectedPurchaseRequests.length === 0 ||
                  updatePurchaseOrderMutation.isPending
                }
              >
                {updatePurchaseOrderMutation.isPending
                  ? "Adding..."
                  : "Add Selected"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Filters Modal */}
      {openFilters && (
        <FiltersModal
          open={openFilters}
          onClose={() => setOpenFilters(false)}
          onApply={() => setOpenFilters(false)}
          filters={filters}
          setFilters={setFilters}
        />
      )}

      {/* Purchase Cart Modal */}
      <PurchaseRequestCart
        open={showPurchaseCart}
        onClose={() => setShowPurchaseCart(false)}
        cartItems={[]}
        branchId={Number(selectedBranch)}
        draftPurchaseRequestID={0}
      />

      {/* PR Details Modal */}
      <PurchaseRequestDetailsModal
        open={!!selectedPRForDetails}
        onClose={() => setSelectedPRForDetails(null)}
        purchaseRequestId={selectedPRForDetails?.prId || ""}
        branchId={selectedPRForDetails?.branchId || ""}
        componentId={import.meta.env.VITE_PUBLIC_COMPONENT_ID || ""}
        tenantId={tenant_id || ""}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        open={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        title="Confirm Add Purchase Requests"
        description={`Are you sure you want to add ${selectedPurchaseRequests.length} selected purchase request(s) to the purchase order? This action cannot be undone.`}
        primaryAction={{
          label: updatePurchaseOrderMutation.isPending
            ? "Adding..."
            : "Add PRs",
          onClick: handleConfirmAdd,
          variant: "primary",
        }}
        secondaryAction={{
          label: "Cancel",
          onClick: () => setShowConfirmationModal(false),
        }}
      />

      {/* Add Create Purchase Order Modal */}
      <CreatePurchaseOrderModal
        open={showCreatePOModal}
        onClose={() => setShowCreatePOModal(false)}
        selectedBranch={selectedBranch}
        allBranchesIds={allBranchesIds}
      />
    </div>
  );
};

export default PurchaseRequest;
